import type { Board } from 'jsxgraph';
import type { LabelPosition } from './label.model';

/**
 * Opposite position lookup for viewport-edge flipping.
 */
const OPPOSITE_POSITION: Record<LabelPosition, LabelPosition> = {
  top: 'bottom',
  bottom: 'top',
  left: 'right',
  right: 'left',
  topLeft: 'bottomRight',
  topRight: 'bottomLeft',
  bottomLeft: 'topRight',
  bottomRight: 'topLeft',
  center: 'center',
  canvasTop: 'canvasBottom',
  canvasBottom: 'canvasTop',
  canvasLeft: 'canvasRight',
  canvasRight: 'canvasLeft',
  canvasTopLeft: 'canvasBottomRight',
  canvasTopRight: 'canvasBottomLeft',
  canvasBottomLeft: 'canvasTopRight',
  canvasBottomRight: 'canvasTopLeft',
  canvasCenter: 'canvasCenter',
  alignedAbove: 'alignedBelow',
  alignedBelow: 'alignedAbove',
  alignedCenter: 'alignedCenter',
};

/**
 * Pick a label position based on the slope (in radians) of the element at the anchor point.
 *
 * The label is placed on the "clear" side — perpendicular to the element direction,
 * biased toward the upper-left so it doesn't overlap the curve/line.
 *
 * Octant mapping (angle measured counter-clockwise from positive x-axis):
 * | Slope range         | Direction  | Label position |
 * |---------------------|------------|----------------|
 * | -π/8  … π/8        | →          | top            |
 * | π/8   … 3π/8       | ↗          | topLeft        |
 * | 3π/8  … 5π/8       | ↑          | left           |
 * | 5π/8  … 7π/8       | ↖          | bottomLeft     |
 * | 7π/8  … π, -π…-7π/8| ←          | bottom         |
 * | -7π/8 … -5π/8      | ↙          | bottomRight    |
 * | -5π/8 … -3π/8      | ↓          | right          |
 * | -3π/8 … -π/8       | ↘          | topRight       |
 */
export function pickPositionFromSlope(slopeRadians: number): LabelPosition {
  // Normalize to [-π, π]
  let a = slopeRadians % (2 * Math.PI);
  if (a > Math.PI) {
    a -= 2 * Math.PI;
  }
  if (a < -Math.PI) {
    a += 2 * Math.PI;
  }

  const eighth = Math.PI / 8;

  if (a >= -eighth && a < eighth) {
    return 'top';
  }
  if (a >= eighth && a < 3 * eighth) {
    return 'topLeft';
  }
  if (a >= 3 * eighth && a < 5 * eighth) {
    return 'left';
  }
  if (a >= 5 * eighth && a < 7 * eighth) {
    return 'bottomLeft';
  }
  if (a >= 7 * eighth || a < -7 * eighth) {
    return 'bottom';
  }
  if (a >= -7 * eighth && a < -5 * eighth) {
    return 'bottomRight';
  }
  if (a >= -5 * eighth && a < -3 * eighth) {
    return 'right';
  }
  // -3π/8 … -π/8
  return 'topRight';
}

/**
 * Fraction of the visible range considered "near the edge".
 * If the anchor is within this margin, the label flips to the opposite side.
 */
const EDGE_MARGIN = 0.08;

/**
 * If the label anchor is near a canvas edge, flip to the opposite side so the label stays visible.
 *
 * @param position - The position picked by slope logic
 * @param anchorX - Anchor x in math coordinates
 * @param anchorY - Anchor y in math coordinates
 * @param board - JSXGraph board (for bounding box)
 * @returns Possibly-flipped position
 */
export function clampToViewport(
  position: LabelPosition,
  anchorX: number,
  anchorY: number,
  board: Board,
): LabelPosition {
  const bbox = board.getBoundingBox(); // [xMin, yMax, xMax, yMin]
  const xMin = bbox[0];
  const yMax = bbox[1];
  const xMax = bbox[2];
  const yMin = bbox[3];

  const xRange = xMax - xMin;
  const yRange = yMax - yMin;
  const mx = EDGE_MARGIN * xRange;
  const my = EDGE_MARGIN * yRange;

  const nearLeft = anchorX - xMin < mx;
  const nearRight = xMax - anchorX < mx;
  const nearTop = yMax - anchorY < my;
  const nearBottom = anchorY - yMin < my;

  // Check if the chosen position would push the label off-screen
  const needsFlip =
    (nearTop && (position === 'top' || position === 'topLeft' || position === 'topRight')) ||
    (nearBottom && (position === 'bottom' || position === 'bottomLeft' || position === 'bottomRight')) ||
    (nearLeft && (position === 'left' || position === 'topLeft' || position === 'bottomLeft')) ||
    (nearRight && (position === 'right' || position === 'topRight' || position === 'bottomRight'));

  return needsFlip ? OPPOSITE_POSITION[position] : position;
}
